/**
 * Collection picker - assigns favorite item(s) to a collection.
 *
 * Opened from the "In a collection..." toast action (star quick-add, single item),
 * the /favorites row move button (single), and the selection toolbar (bulk).
 * SINGLE mode (ctx.itemId) -> PATCH /items/<id>; BULK mode (ctx.itemIds) -> POST
 * /items/move. Bottom sheet on touch, centered panel on desktop.
 *
 * Extras: a live SEARCH field appears when the user has > SEARCH_THRESHOLD
 * collections (matches by name, shows the parent path for disambiguation); every
 * collection row carries a "use as default" pin (POST/DELETE .../default) so new
 * favorites (star quick-add) land in the chosen collection.
 */

import { showFavToast } from './favorites-state.js';

const SEARCH_THRESHOLD = 10;

function tr(key, fallback) {
	try {
		const lang = window.LANG || 'EN';
		const text = window.TranslationRegistry && window.TranslationRegistry.translate(key, lang);
		return (text && text !== key) ? text : fallback;
	} catch (e) { return fallback; }
}

function closeFavPicker() {
	$('.fav-picker-overlay').remove();
	$(document).off('keydown.favPicker');
}

// DFS order with depth for indentation.
function orderTree(collections) {
	const byParent = {};
	collections.forEach(c => {
		const key = c.parent_id == null ? 'root' : String(c.parent_id);
		(byParent[key] = byParent[key] || []).push(c);
	});
	const out = [];
	const walk = (parentKey, depth) => {
		(byParent[parentKey] || []).forEach(c => {
			out.push({ id: c.id, name: c.name, depth: depth });
			walk(String(c.id), depth + 1);
		});
	};
	walk('root', 0);
	return out;
}

// Returns a function: collection -> array of ancestor+self names (for a path label).
function pathResolver(collections) {
	const byId = {};
	collections.forEach(c => { byId[c.id] = c; });
	return function (c) {
		const parts = [];
		let cur = c, guard = 0;
		while (cur && guard++ < 12) {
			parts.unshift(cur.name);
			cur = (cur.parent_id != null) ? byId[cur.parent_id] : null;
		}
		return parts;
	};
}

// Assign one item (PATCH) or a whole selection (bulk POST) to a collection.
function assignSelection(ctx, collectionId, collectionName) {
	const isBulk = Array.isArray(ctx.itemIds);
	const url = isBulk ? '/api/favorites/items/move' : '/api/favorites/items/' + ctx.itemId;
	const body = isBulk
		? { ids: ctx.itemIds, collection_id: collectionId }
		: { collection_id: collectionId };
	return fetch(url, {
		method: isBulk ? 'POST' : 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify(body)
	}).then(async (res) => {
		const js = await res.json().catch(() => ({}));
		if (res.ok && js.success) {
			let msg;
			if (isBulk) {
				const d = js.data || {};
				const n = (typeof d.moved === 'number') ? (d.moved + (d.merged || 0)) : ctx.itemIds.length;
				msg = collectionId == null
					? tr('fav_moved_n_root', 'Moved {n} to Favorites root').replace('{n}', n)
					: tr('fav_moved_n_to', 'Moved {n} to: {name}').replace('{n}', n).replace('{name}', collectionName || '');
			} else {
				msg = collectionId == null
					? tr('fav_moved_root', 'Moved to Favorites root')
					: tr('fav_moved_to', 'Moved to collection: {name}').replace('{name}', collectionName || '');
			}
			showFavToast(msg, 'ok');
			return true;
		}
		showFavToast(tr('fav_error', 'Favorites action failed. Please try again.'), 'warn');
		return false;
	}).catch(() => {
		showFavToast(tr('fav_error', 'Favorites action failed. Please try again.'), 'warn');
		return false;
	});
}

function createCollection(name) {
	return fetch('/api/favorites/collections', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify({ name: name })
	}).then(async (res) => {
		const js = await res.json().catch(() => ({}));
		if (res.ok && js.success) return js.data;
		showFavToast(js.error === 'limit_exceeded'
			? tr('fav_limit', 'Favorites limit reached — remove something first')
			: tr('fav_error', 'Favorites action failed. Please try again.'), 'warn');
		return null;
	}).catch(() => { showFavToast(tr('fav_error', 'Favorites action failed. Please try again.'), 'warn'); return null; });
}

function setDefaultRequest(collectionId) {
	return fetch('/api/favorites/collections/' + collectionId + '/default',
		{ method: 'POST', credentials: 'same-origin' }).then(r => r.ok).catch(() => false);
}
function clearDefaultRequest() {
	return fetch('/api/favorites/collections/default',
		{ method: 'DELETE', credentials: 'same-origin' }).then(r => r.ok).catch(() => false);
}

/**
 * Open the picker for one item ({itemId,...}) or a selection ({itemIds:[...]}).
 * onDone fires after a successful assignment.
 */
export function openFavPicker(ctx) {
	if (!ctx || (!ctx.itemId && !Array.isArray(ctx.itemIds))) return;
	closeFavPicker();
	const done = (ok) => { if (ok && typeof ctx.onDone === 'function') ctx.onDone(); };

	const $overlay = $('<div class="fav-picker-overlay"></div>');
	const $sheet = $('<div class="fav-picker" role="menu"></div>');
	$sheet.append($('<div class="fav-picker__title"></div>')
		.text(tr('fav_picker_title', 'Choose a collection')));
	const $searchWrap = $('<div class="fav-picker__search-wrap"></div>').hide();
	const $search = $('<input type="text" class="fav-picker__search" autocomplete="off">')
		.attr('placeholder', tr('fav_picker_search', 'Search collections…'));
	$searchWrap.append($('<i class="bi bi-search fav-picker__search-icon"></i>')).append($search);
	$sheet.append($searchWrap);
	const $list = $('<div class="fav-picker__list"></div>');
	$sheet.append($list);
	const $newWrap = $('<div class="fav-picker__new-wrap"></div>');
	$sheet.append($newWrap);

	let collections = [];
	let defaultId = null;
	let pathOf = () => [];

	const assignTo = (collectionId, collectionName) => {
		closeFavPicker();
		assignSelection(ctx, collectionId, collectionName).then(done);
	};

	const toggleDefault = (collectionId) => {
		const isDef = defaultId === collectionId;
		(isDef ? clearDefaultRequest() : setDefaultRequest(collectionId)).then(ok => {
			if (!ok) { showFavToast(tr('fav_error', 'Favorites action failed. Please try again.'), 'warn'); return; }
			defaultId = isDef ? null : collectionId;
			showFavToast(isDef
				? tr('fav_default_cleared', 'Default cleared')
				: tr('fav_default_set', 'Default collection set'), 'ok');
			renderList($search.val());
			if (typeof ctx.onDefaultChange === 'function') ctx.onDefaultChange(defaultId);
		});
	};

	// Row = div[role=button] so we can nest a REAL pin <button> (22e: no interactive
	// content inside a <button>).
	const makeRow = (node, depth, label, subLabel) => {
		const $row = $('<div class="fav-picker__row" role="button" tabindex="0"></div>')
			.attr('style', '--fav-indent:' + (depth < 0 ? 0 : depth));
		$row.append($('<i class="bi"></i>').addClass(depth === -1 ? 'bi-star' : 'bi-folder'));
		const $txt = $('<span class="fav-picker__row-text"></span>');
		$txt.append($('<span class="fav-picker__row-name"></span>').text(label));
		if (subLabel) $txt.append($('<span class="fav-picker__row-path"></span>').text(subLabel));
		$row.append($txt);
		if (node) {
			const on = defaultId === node.id;
			const $pin = $('<button type="button" class="fav-picker__default-btn"></button>')
				.toggleClass('is-default', on)
				.attr('title', on
					? tr('fav_picker_default_clear', 'Default — click to clear')
					: tr('fav_picker_set_default', 'Use as default (new favorites go here)'))
				.append($('<i class="bi"></i>').addClass(on ? 'bi-pin-fill' : 'bi-pin-angle'));
			$pin.on('click', (e) => { e.stopPropagation(); toggleDefault(node.id); });
			$row.append($pin);
		}
		return $row;
	};

	function renderList(query) {
		$list.empty();
		const q = (query || '').trim().toLowerCase();
		if (!q) {
			const $root = makeRow(null, -1, tr('fav_picker_root', 'Favorites (no collection)'));
			$root.on('click', () => assignTo(null, null));
			$root.on('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); assignTo(null, null); } });
			$list.append($root);
			orderTree(collections).forEach(node => {
				const $r = makeRow(node, node.depth, node.name);
				$r.on('click', () => assignTo(node.id, node.name));
				$r.on('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); assignTo(node.id, node.name); } });
				$list.append($r);
			});
			return;
		}
		const matches = collections.filter(c => (c.name || '').toLowerCase().includes(q));
		if (!matches.length) {
			$list.append($('<div class="fav-picker__empty"></div>')
				.text(tr('fav_picker_no_matches', 'No collections found')));
			return;
		}
		matches.forEach(c => {
			const parts = pathOf(c);
			const sub = parts.length > 1 ? parts.slice(0, -1).join(' / ') : '';
			const $r = makeRow(c, 0, c.name, sub);
			$r.on('click', () => assignTo(c.id, c.name));
			$r.on('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); assignTo(c.id, c.name); } });
			$list.append($r);
		});
	}

	fetch('/api/favorites/tree', { credentials: 'same-origin' })
		.then(res => res.ok ? res.json() : null)
		.then(js => {
			const data = (js && js.data) || {};
			collections = data.collections || [];
			defaultId = data.default_collection_id || null;
			pathOf = pathResolver(collections);

			if (collections.length > SEARCH_THRESHOLD) {
				$searchWrap.show();
				$search.on('input', () => renderList($search.val()));
				setTimeout(() => { try { $search.trigger('focus'); } catch (e) {} }, 60);
			}
			renderList('');

			// Inline "new collection" (created at root, then assigns; prefills the search text).
			const $newRow = $('<div class="fav-picker__new"></div>');
			const $input = $('<input type="text" class="fav-picker__new-input" maxlength="100">')
				.attr('placeholder', tr('fav_picker_new', 'New collection...'));
			const $ok = $('<button type="button" class="fav-picker__new-ok"><i class="bi bi-plus-lg"></i></button>');
			const submit = function () {
				const name = ($input.val() || '').trim();
				if (!name) return;
				$ok.prop('disabled', true);
				createCollection(name).then(created => {
					$ok.prop('disabled', false);
					if (!created) return;
					assignTo(created.id, created.name);
				});
			};
			$ok.on('click', submit);
			$input.on('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
			$newWrap.append($newRow.append($input).append($ok));
		})
		.catch(() => {
			showFavToast(tr('fav_error', 'Favorites action failed. Please try again.'), 'warn');
		});

	const $cancel = $('<button type="button" class="fav-picker__cancel"></button>')
		.text(tr('fav_picker_cancel', 'Cancel'));
	$cancel.on('click', closeFavPicker);
	$sheet.append($cancel);

	$overlay.on('click', function (e) { if (e.target === this) closeFavPicker(); });
	$(document).off('keydown.favPicker').on('keydown.favPicker', function (e) {
		if (e.key === 'Escape') closeFavPicker();
	});
	$overlay.append($sheet);
	$('body').append($overlay);
}
